<template>
    <div
        v-if="!loading"
        class="d-flex align-items-center justify-content-center vh-100"
    >
        <div class="card p-4" style="width: 100%; max-width: 450px">
            <form class="form-horizonal" @submit.prevent="handleRegister">
                <div class="card-header text-center mb-4">
                    <img
                        :src="systemLogo"
                        alt="Logo"
                        class="img-fluid"
                        style="max-width: 150px"
                    />
                    <h4 class="mt-3">
                        {{ systemTitle }} -
                        {{ $t('login.login_button__register') }}
                    </h4>
                </div>
                <div class="card-body">
                    <div class="form-group mb-3">
                        <label for="email" class="form-label">
                            {{ $t('users.users_create__username') }}
                        </label>
                        <input
                            id="email"
                            type="email"
                            v-model.trim="email"
                            class="form-control"
                            :placeholder="
                                $t('users.users_create__username_placeholder')
                            "
                            autocomplete="off"
                            required
                        />
                    </div>

                    <div class="form-group mb-3">
                        <label for="password" class="form-label">
                            {{
                                $t(
                                    'users.users_label__password_form_new_password'
                                )
                            }}
                        </label>
                        <div class="input-group">
                            <input
                                :type="passwordVisible ? 'text' : 'password'"
                                id="password"
                                v-model="password"
                                class="form-control"
                                autocomplete="off"
                                :placeholder="
                                    $t('login.login_msg__placeholder_password')
                                "
                                required
                                @blur="handleNewPasswordBlur"
                            />
                            <div class="input-group-append">
                                <button
                                    type="button"
                                    class="btn btn-outline-primary"
                                    @click="togglePasswordVisibility"
                                >
                                    <i
                                        :class="
                                            passwordVisible
                                                ? 'fa fa-eye-slash'
                                                : 'fa fa-eye'
                                        "
                                        style="color: #427c89"
                                    ></i>
                                </button>
                            </div>
                        </div>
                        <div
                            v-if="passwordRuleErrorMessages.length > 0"
                            class="alert alert-danger mt-3"
                            role="alert"
                        >
                            <ul class="mb-0">
                                <li
                                    v-for="msg in passwordRuleErrorMessages"
                                    :key="msg"
                                >
                                    {{ msg }}
                                </li>
                            </ul>
                        </div>
                    </div>

                    <div class="form-group mb-3">
                        <label for="confirmPassword" class="form-label">
                            {{
                                $t(
                                    'users.users_label__password_form_confirm_password'
                                )
                            }}
                        </label>
                        <div class="input-group">
                            <input
                                :type="
                                    confirmPasswordVisible ? 'text' : 'password'
                                "
                                id="confirmPassword"
                                v-model="confirmPassword"
                                class="form-control"
                                autocomplete="off"
                                :placeholder="
                                    $t('login.login_msg__placeholder_password')
                                "
                                required
                            />
                            <div class="input-group-append">
                                <button
                                    type="button"
                                    class="btn btn-outline-primary"
                                    @click="toggleConfirmPasswordVisibility"
                                >
                                    <i
                                        :class="
                                            confirmPasswordVisible
                                                ? 'fa fa-eye-slash'
                                                : 'fa fa-eye'
                                        "
                                        style="color: #427c89"
                                    ></i>
                                </button>
                            </div>
                        </div>
                        <small v-if="passwordMismatch" class="text-danger">
                            {{
                                $t(
                                    'users.users_msg__password_form_new_password_do_not_match_confirm_password'
                                ) || 'Passwords do not match.'
                            }}
                        </small>
                    </div>

                    <button
                        type="submit"
                        class="btn btn-primary w-100"
                        :disabled="submitting || !formValid"
                    >
                        {{
                            $t('users.users_msg__user_form_submit') ||
                            'Register'
                        }}
                    </button>
                </div>
            </form>

            <div class="d-flex justify-content-between mt-3">
                <button class="btn btn-link p-0" @click="goToSignin">
                    <i class="fa fa-arrow-left"></i>
                    {{ $t('login.login_msg__sign_in') }}
                </button>
                <button class="btn btn-link p-0" @click="privacyPolicy">
                    <i class="fa fa-shield"></i>
                    {{ $t('login.login_msg__privacy_policy') }}
                </button>
            </div>

            <LanguageSelector
                class="mt-3"
                :languages="languages"
                :currentLanguage="currentLanguage"
                @language-changed="changeLanguage"
            />
            <div
                v-if="errorMessage"
                class="alert alert-danger mt-3"
                role="alert"
            >
                {{ errorMessage }}
            </div>
        </div>
    </div>
</template>

<script>
import { computed, ref, onMounted, inject } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import i18n from '@/api/i18n';
import apiClient from '@/api/index';
import { setLoading } from '@/plugins/loading';
import LanguageSelector from '@/components/Navbar/LanguageSelector.vue';
import { useLocale } from '@/composables/useLocale';
import { checkPasswordRule, registerUser } from '@/api/users';

export default {
    name: 'RegisterView',
    components: { LanguageSelector },
    setup() {
        const { t } = useI18n();
        const store = useStore();
        const router = useRouter();
        const Swal = inject('$swal');
        const { currentLanguage, languages, changeLanguage, fetchLanguages } =
            useLocale();

        const email = ref('');
        const password = ref('');
        const confirmPassword = ref('');
        const passwordVisible = ref(false);
        const confirmPasswordVisible = ref(false);

        const loading = ref(true);
        const submitting = ref(false);
        const errorMessage = ref('');
        const successMessage = ref('');

        setLoading(true);

        const isUserRegistrationEnabled = computed(
            () => store.getters['system/isUserRegistrationEnabled']
        );

        const passwordMismatch = computed(
            () =>
                password.value &&
                confirmPassword.value &&
                password.value !== confirmPassword.value
        );

        const passwordRuleErrorMessages = ref([]);

        const formValid = computed(() => {
            return (
                email.value &&
                password.value &&
                confirmPassword.value &&
                !passwordMismatch.value &&
                passwordRuleErrorMessages.value.length === 0
            );
        });

        const togglePasswordVisibility = () => {
            passwordVisible.value = !passwordVisible.value;
        };

        const toggleConfirmPasswordVisibility = () => {
            confirmPasswordVisible.value = !confirmPasswordVisible.value;
        };

        const validatePasswordRule = async () => {
            // 與 PasswordForm.vue 一致的檢查流程
            if (!password.value) {
                passwordRuleErrorMessages.value = [];
                return;
            }
            setLoading(true);
            try {
                const response = await checkPasswordRule(password.value);
                if (response && response.errors) {
                    passwordRuleErrorMessages.value = response.errors;
                } else {
                    passwordRuleErrorMessages.value = [];
                }
            } catch (error) {
                if (error?.response?.data?.error) {
                    passwordRuleErrorMessages.value = error.response.data.error;
                } else {
                    passwordRuleErrorMessages.value = [
                        t('django.system_msg__unexpected_error_occurred'),
                    ];
                }
            } finally {
                setLoading(false);
            }
        };

        const handleNewPasswordBlur = async () => {
            await validatePasswordRule();
        };

        const handleRegister = async () => {
            errorMessage.value = '';
            successMessage.value = '';

            // 若註冊被關閉，導回登入
            if (!isUserRegistrationEnabled.value) {
                goToSignin();
                return;
            }

            // 先檢查密碼規則與是否一致
            await validatePasswordRule();
            if (passwordRuleErrorMessages.value.length > 0) return;

            if (passwordMismatch.value) {
                errorMessage.value =
                    t(
                        'users.users_msg__password_form_new_password_do_not_match_confirm_password'
                    ) || 'Passwords do not match.';
                return;
            }

            try {
                submitting.value = true;
                setLoading(true);
                const { status } = await registerUser({
                    username: email.value,
                    password: password.value,
                });

                if (status === 200 || status === 201) {
                    successMessage.value = t('login.register_msg__success');
                    await Swal?.fire({
                        title: t('login.register_title__success'),
                        icon: 'success',
                        showConfirmButton: true,
                        html: successMessage.value,
                    });
                    goToSignin();
                } else {
                    throw new Error('register_failed');
                }
            } catch (err) {
                let message =
                    err?.response?.status === 400
                        ? t('login.register_msg__account_exists')
                        : (err?.response?.data?.detail &&
                              String(err.response.data.detail)) ||
                          t('login.register_msg__failed') ||
                          'Registration failed.';

                await Swal?.fire({
                    title: t('login.register_msg__failed'),
                    icon: 'error',
                    showConfirmButton: true,
                    html: message,
                });
            } finally {
                submitting.value = false;
                setLoading(false);
            }
        };

        const goToSignin = () => router.push('/');
        const privacyPolicy = () => router.push('/privacy-policy');

        onMounted(async () => {
            const locale =
                localStorage.getItem('locale') ||
                store.getters['system/defaultLocale'];

            i18n.global.locale.value = locale;
            await store.dispatch('i18n/loadLocale', locale);

            const responseLang = await apiClient.get(
                `/languages/lang/${locale}?disable_cache=false`,
                { skipAuthorization: true }
            );
            i18n.global.setLocaleMessage(locale, responseLang.data);

            await fetchLanguages();

            loading.value = false;
            setLoading(false);
        });

        const systemLogo = computed(() => {
            const logoBase64 = store.getters['system/logo'];
            return logoBase64
                ? `data:image/png;base64,${logoBase64}`
                : `${process.env.BASE_URL}assets/img/dhc-logo.png`;
        });

        const systemTitle = computed(() => {
            return store.getters['system/title'] || 'Club Assist SaaS';
        });

        return {
            t,
            // state
            email,
            password,
            confirmPassword,
            passwordVisible,
            confirmPasswordVisible,
            loading,
            submitting,
            errorMessage,
            successMessage,
            // computed
            systemLogo,
            systemTitle,
            currentLanguage,
            languages,
            passwordMismatch,
            passwordRuleErrorMessages,
            formValid,
            // methods
            changeLanguage,
            togglePasswordVisibility,
            toggleConfirmPasswordVisibility,
            handleRegister,
            goToSignin,
            privacyPolicy,
            handleNewPasswordBlur,
        };
    },
};
</script>
